import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AstrologyApp from './App';

// Lazy load components for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const KundliCalculator = lazy(() => import('./pages/calculators/KundliCalculator'));
const MoonSignCalculator = lazy(() => import('./pages/calculators/MoonSignCalculator'));
const LagnaCalculator = lazy(() => import('./pages/calculators/LagnaCalculator'));
const NumerologyCalculator = lazy(() => import('./pages/calculators/NumerologyCalculator'));
const LifePathCalculator = lazy(() => import('./pages/calculators/LifePathCalculator'));
const NameNumerology = lazy(() => import('./pages/calculators/NameNumerology'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const BlogIndex = lazy(() => import('./pages/blog/BlogIndex'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const PanchangPage = lazy(() => import('./pages/PanchangPage'));
const RashifalPage = lazy(() => import('./pages/RashifalPage'));
const ProfileManagement = lazy(() => import('./pages/ProfileManagement'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const OnboardingProfilePage = lazy(() => import('./pages/OnboardingProfilePage'));

const AppRouter = () => {
  return (
    <HelmetProvider>
      <Router basename="/app">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Root of /app - Kundli calculator */}
            <Route path="/" element={<Layout><AstrologyApp /></Layout>} />
            {/* Other routes (e.g. /app/panchang, /app/shop) */}
            <Route path="/home" element={<Layout><HomePage /></Layout>} />
            <Route path="/videos" element={<Layout><VideosPage /></Layout>} />
            <Route path="/panchang" element={<Layout><PanchangPage /></Layout>} />
            <Route path="/rashifal" element={<Layout><RashifalPage /></Layout>} />
            <Route path="/profile" element={<Layout><ProfileManagement /></Layout>} />
            {/* NEW — dedicated post-signup onboarding page for the web app only.
                Does not touch /profile (ProfileManagement) used elsewhere. */}
            <Route path="/onboarding/profile" element={<OnboardingProfilePage />} />
            <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
            <Route path="/shop/:slug" element={<Layout><ProductDetailPage /></Layout>} />
            <Route path="/kundli-calculator" element={<Layout><KundliCalculator /></Layout>} />
            <Route path="/moon-sign-calculator" element={<Layout><MoonSignCalculator /></Layout>} />
            <Route path="/lagna-calculator" element={<Layout><LagnaCalculator /></Layout>} />
            <Route path="/numerology-calculator" element={<Layout><NumerologyCalculator /></Layout>} />
            <Route path="/life-path-calculator" element={<Layout><LifePathCalculator /></Layout>} />
            <Route path="/name-numerology" element={<Layout><NameNumerology /></Layout>} />
            <Route path="/features" element={<Layout><FeaturesPage /></Layout>} />
            <Route path="/blog" element={<Layout><BlogIndex /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
            <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
            <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
            <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
            <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />
            <Route path="/refund-policy" element={<Layout><RefundPolicy /></Layout>} />
            <Route path="/disclaimer" element={<Layout><Disclaimer /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />

            {/* 404 Route */}
            <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
          </Routes>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
};

// Simple 404 component
const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-saffron-600 mb-4">404</h1>
      <p className="text-xl text-saffron-700 mb-8">Page not found</p>
      <a href="/app" className="text-saffron-600 hover:text-saffron-800 underline">
        Return to App
      </a>
    </div>
  </div>
);

export default AppRouter;
